import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getPaymentTerms, getPaymentTerm, createPaymentTerm, updatePaymentTerm, deletePaymentTerm,
  getPaymentTermEditability, bindPaymentTerm, getPOs, getTemplates,
  renewSubscription, approveRenewal, rejectRenewal, cancelSubscription, upsertSubscriptionConfig,
} from '../services/api';
import { formatCurrency, formatDate, StatusBadge, TERM_TYPES, TRIGGER_TYPES, ANCHOR_TYPES } from '../utils/helpers';
import { Plus, ChevronRight, Trash2, PlusCircle, Link2, BookOpen, Pencil, RefreshCw, CheckCircle, XCircle } from 'lucide-react';

const RENEWAL_MODES = [
  { value: 'AUTO', label: 'Auto Renew' },
  { value: 'BUYER_APPROVAL_REQUIRED', label: 'Manual Approval' },
  { value: 'MANUAL', label: 'Manual (No Auto Renewal)' },
];

function periodicityToBillingCycle(periodicity) {
  const map = {
    DAILY: 'MONTHLY',
    WEEKLY: 'MONTHLY',
    MONTHLY: 'MONTHLY',
    QUARTERLY: 'QUARTERLY',
    YEARLY: 'ANNUAL',
  };
  return map[periodicity] || 'MONTHLY';
}

function inferSubscriptionDates(installments) {
  const dates = (installments || [])
    .map(i => (i.scheduled_invoice_date ? String(i.scheduled_invoice_date).split('T')[0] : null))
    .filter(Boolean)
    .sort();
  return {
    start_date: dates[0] || '',
    end_date: dates[dates.length - 1] || '',
  };
}

function buildSubscriptionConfigPayload(subConfig) {
  const contractYears = subConfig.period_from && subConfig.period_to
    ? Math.max(1, Math.ceil(
      (new Date(subConfig.period_to) - new Date(subConfig.period_from)) / (365.25 * 86400000)
    ))
    : 1;
  return {
    billing_cycle: periodicityToBillingCycle(subConfig.periodicity),
    billing_in_advance: subConfig.billing_in_advance !== false,
    start_date: subConfig.period_from,
    end_date: subConfig.period_to || null,
    contract_years: contractYears,
    yoy_increment_pct: subConfig.yoy_increment_pct ? parseFloat(subConfig.yoy_increment_pct) : null,
    renewal_mode: subConfig.renewal_mode || 'AUTO',
    renewal_notice_days: parseInt(subConfig.renewal_notice_days, 10) || 30,
    cancellation_notice_days: parseInt(subConfig.cancellation_notice_days, 10) || 30,
  };
}

function renewalModeLabel(mode) {
  return RENEWAL_MODES.find(m => m.value === mode)?.label || mode?.replace(/_/g, ' ') || '—';
}
import toast from 'react-hot-toast';
import { AppModal, ConfirmModal } from '../components/common/AppModal';

// ═══════════════════════════════════════════════════════════════
// LIST
// ═══════════════════════════════════════════════════════════════
export function PaymentTermList() {
  const [terms, setTerms]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    getPaymentTerms().then(r => setTerms(r.data.terms || [])).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Payment Terms</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Create payment terms here, or go to a <button onClick={() => navigate('/purchase-orders')} className="text-primary-600 hover:underline">Purchase Order</button> to add payment terms directly to it.
          </p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary">
          <Plus size={16} /> New Payment Term
        </button>
      </div>

      {showCreate && (
        <TermFormModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}

      <div className="card overflow-hidden">
        <table className="w-full">
          <thead>
            <tr>
              {['Name', 'Type', 'Linked SO', 'Installments', 'Status', 'Template', 'Created', ''].map(h => (
                <th key={h} className="table-th">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="table-td text-center text-gray-400">Loading…</td></tr>
            ) : terms.length === 0 ? (
              <tr><td colSpan={8} className="table-td text-center py-10">
                <BookOpen size={32} className="mx-auto text-gray-300 mb-2" />
                <p className="text-gray-400">No payment terms yet</p>
                <p className="text-xs text-gray-400 mt-1">You can also create payment terms directly from a Purchase Order</p>
              </td></tr>
            ) : terms.map(t => (
              <tr key={t.term_id} className="hover:bg-gray-50 cursor-pointer"
                onClick={() => navigate(`/payment-terms/${t.term_id}`)}>
                <td className="table-td font-medium">{t.name}</td>
                <td className="table-td text-xs text-gray-500">{t.term_type?.replace(/_/g, ' ')}</td>
                <td className="table-td text-xs">
                  {t.po_id
                    ? <span className="text-primary-600 font-mono">{t.po_number || t.po_id.slice(0, 8) + '…'}</span>
                    : <span className="text-gray-400 italic">Not linked</span>}
                </td>
                <td className="table-td text-center text-gray-600">{t.installment_count || '—'}</td>
                <td className="table-td"><StatusBadge status={t.status} /></td>
                <td className="table-td">
                  {t.is_template ? <span className="badge bg-blue-100 text-blue-700">Template</span> : '—'}
                </td>
                <td className="table-td text-gray-500">{formatDate(t.created_at)}</td>
                <td className="table-td"><ChevronRight size={16} className="text-gray-400" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// DETAIL
// ═══════════════════════════════════════════════════════════════
export function PaymentTermDetail() {
  const { id } = useParams();
  const [term, setTerm]           = useState(null);
  const [pos, setPOs]             = useState([]);
  const [selectedPO, setSelectedPO] = useState('');
  const [binding, setBinding]     = useState(false);
  const [loading, setLoading]     = useState(true);
  const [editability, setEditability] = useState(null);
  const [showEdit, setShowEdit]   = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    Promise.all([getPaymentTerm(id), getPOs(), getPaymentTermEditability(id).catch(() => ({ data: { editable: false } }))])
      .then(([t, p, e]) => {
        setTerm(t.data.term);
        setPOs(p.data.purchase_orders || []);
        setEditability(e.data);
      })
      .finally(() => setLoading(false));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const handleBind = async () => {
    if (!selectedPO) return toast.error('Select a Purchase Order first');
    setBinding(true);
    try {
      await bindPaymentTerm(id, selectedPO);
      toast.success('Payment term linked to Sales Order. Installment amounts computed.');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to link');
    } finally {
      setBinding(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await deletePaymentTerm(id);
      toast.success('Payment term deleted');
      navigate('/payment-terms');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete payment term');
    } finally {
      setDeleteLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  if (loading) return <div className="animate-pulse h-96 bg-gray-200 rounded-xl" />;
  if (!term) return <div className="text-center text-gray-500 py-12">Payment term not found</div>;

  // POs that don't already have a payment term
  const availablePOs = pos.filter(p => p.status !== 'CANCELLED');
  const canEdit = editability?.editable;

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <button onClick={() => navigate('/payment-terms')} className="hover:text-primary-600">
            Payment Terms
          </button>
          <ChevronRight size={14} />
          <span className="text-gray-900 font-medium">{term.name}</span>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <button onClick={() => setShowEdit(true)} className="btn-secondary text-sm">
              <Pencil size={14} /> Edit
            </button>
            <button onClick={() => setShowDeleteConfirm(true)} className="btn-secondary text-sm text-red-600 border-red-200 hover:bg-red-50">
              <Trash2 size={14} /> Delete
            </button>
          </div>
        )}
        {editability && !editability.editable && editability.reason && (
          <span className="text-xs text-gray-400 max-w-xs text-right" title={editability.reason}>
            Edit locked — invoice approved in O2C
          </span>
        )}
      </div>

      {/* Header */}
      <div className="card p-6">
        <div className="flex items-start justify-between mb-5">
          <div>
            <h1 className="text-2xl font-bold">{term.name}</h1>
            {term.description && <p className="text-gray-500 mt-1">{term.description}</p>}
          </div>
          <StatusBadge status={term.status} />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          {[
            { label: 'Type',          value: term.term_type?.replace(/_/g, ' ') },
            { label: 'SO Value',      value: term.total_po_value ? formatCurrency(term.total_po_value, term.currency) : '—' },
            { label: 'Currency',      value: term.currency },
            { label: 'Version',       value: term.version },
            { label: 'Template',      value: term.is_template ? 'Yes' : 'No' },
            { label: 'Invoice Mode',  value: term.invoice_approval_mode?.replace(/_/g, ' ') || '—' },
            { label: 'Created',       value: formatDate(term.created_at) },
          ].map(f => (
            <div key={f.label}>
              <div className="text-xs text-gray-400">{f.label}</div>
              <div className="font-medium mt-0.5">{f.value}</div>
            </div>
          ))}
        </div>

        {/* Link to Sales Order */}
        {!term.po_id ? (
          <div className="mt-5 pt-5 border-t border-gray-100">
            <p className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-1.5">
              <Link2 size={15} className="text-primary-500" />
              Link this payment term to a Purchase Order
            </p>
            <div className="flex items-center gap-3 flex-wrap">
              <select
                className="select flex-1 max-w-sm"
                value={selectedPO}
                onChange={e => setSelectedPO(e.target.value)}
              >
                <option value="">— Select a Purchase Order —</option>
                {availablePOs.map(p => (
                  <option key={p.po_id} value={p.po_id}>
                    {p.po_number} — {p.title} ({p.buyer_name || 'Unknown customer'}) — {formatCurrency(p.po_value, p.currency)}
                  </option>
                ))}
              </select>
              <button
                onClick={handleBind}
                disabled={!selectedPO || binding}
                className="btn-primary"
              >
                <Link2 size={15} />
                {binding ? 'Linking…' : 'Link to Sales Order'}
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Linking computes the exact INR/currency amount for each installment based on the SO value.
            </p>
          </div>
        ) : (
          <div className="mt-5 pt-5 border-t border-gray-100">
            <div className="flex items-center gap-2 text-sm">
              <Link2 size={14} className="text-green-500" />
              <span className="text-gray-500">Linked to Sales Order:</span>
              <button
                onClick={() => navigate(`/purchase-orders/${term.po_id}`)}
                className="text-primary-600 font-medium hover:underline"
              >
                {pos.find(p => p.po_id === term.po_id)?.po_number || term.po_id.slice(0, 8)}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Installments */}
      <div className="card">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <h3 className="font-semibold">Installments</h3>
          {term.total_po_value && (
            <span className="text-sm text-gray-500">
              SO Value: <strong>{formatCurrency(term.total_po_value, term.currency)}</strong>
            </span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                {['#', 'Label', 'Amount', 'Computed Amount', 'Trigger', 'Anchor', 'Offset', 'Grace', 'Approval', 'Status'].map(h => (
                  <th key={h} className="table-th">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!term.installments?.length ? (
                <tr><td colSpan={10} className="table-td text-center text-gray-400 py-6">No installments defined</td></tr>
              ) : term.installments.map(inst => (
                <tr key={inst.installment_id} className="hover:bg-gray-50">
                  <td className="table-td text-gray-400 text-xs">{inst.sequence_no}</td>
                  <td className="table-td font-medium">{inst.label}</td>
                  <td className="table-td font-semibold text-primary-700">
                    {inst.amount_type === 'PERCENTAGE'
                      ? `${parseFloat(inst.amount_value).toFixed(2)}%`
                      : formatCurrency(inst.amount_value, term.currency)}
                  </td>
                  <td className="table-td font-semibold">
                    {inst.computed_amount
                      ? <span className="text-green-700">{formatCurrency(inst.computed_amount, term.currency)}</span>
                      : <span className="text-gray-400 text-xs italic">After linking</span>}
                  </td>
                  <td className="table-td text-xs text-gray-500">{inst.trigger_type?.replace(/_/g, ' ')}</td>
                  <td className="table-td text-xs text-gray-500">{inst.anchor_date_type?.replace(/_/g, ' ')}</td>
                  <td className="table-td text-xs">{inst.due_offset_days}d</td>
                  <td className="table-td text-xs">{inst.grace_period_days}d</td>
                  <td className="table-td text-xs">
                    {inst.requires_buyer_approval
                      ? <span className="badge bg-amber-100 text-amber-700">Required</span>
                      : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="table-td"><StatusBadge status={inst.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Percentage total validation */}
        {term.installments?.length > 0 && term.installments.every(i => i.amount_type === 'PERCENTAGE') && (
          <div className="px-5 py-3 bg-gray-50 border-t text-sm">
            {(() => {
              const total = term.installments.reduce((s, i) => s + parseFloat(i.amount_value || 0), 0);
              const valid = Math.abs(total - 100) <= 0.01;
              return (
                <span className={valid ? 'text-green-600' : 'text-red-600'}>
                  Total: {total.toFixed(2)}% {valid ? '✅ Sums to 100%' : '⚠️ Must sum to 100%'}
                </span>
              );
            })()}
          </div>
        )}
      </div>

      {/* Milestones */}
      {term.milestones?.length > 0 && (
        <div className="card">
          <div className="px-5 py-4 border-b">
            <h3 className="font-semibold">Milestones</h3>
          </div>
          <div className="divide-y divide-gray-100">
            {term.milestones.map(m => (
              <div key={m.milestone_id} className="px-5 py-4 flex items-center justify-between">
                <div>
                  <div className="font-medium">{m.name}</div>
                  <div className="text-sm text-gray-500 mt-0.5">{m.description}</div>
                  {m.target_date && (
                    <div className="text-xs text-gray-400 mt-1">Target: {formatDate(m.target_date)}</div>
                  )}
                </div>
                <StatusBadge status={m.acceptance_status} />
              </div>
            ))}
          </div>
        </div>
      )}

      {term.term_type === 'SUBSCRIPTION' && (
        <SubscriptionRenewalPanel term={term} onRefresh={load} />
      )}

      {showEdit && (
        <TermEditModal
          term={term}
          onClose={() => setShowEdit(false)}
          onSaved={() => { setShowEdit(false); load(); }}
        />
      )}

      {showDeleteConfirm && (
        <ConfirmModal
          title="Delete Payment Term?"
          message="This removes the payment term from PTE. Linked invoices must be cancelled first. O2C will be updated on the next SO sync."
          confirmLabel="Delete"
          loading={deleteLoading}
          danger
          onConfirm={handleDelete}
          onClose={() => setShowDeleteConfirm(false)}
        />
      )}
    </div>
  );
}

function SubscriptionRenewalPanel({ term, onRefresh }) {
  const cfg = term.subscription_config;
  const inferred = inferSubscriptionDates(term.installments);
  const [setupForm, setSetupForm] = useState({
    start_date: inferred.start_date,
    end_date: inferred.end_date,
    billing_cycle: 'MONTHLY',
    billing_in_advance: true,
    renewal_mode: 'AUTO',
    renewal_notice_days: 30,
    cancellation_notice_days: 30,
    yoy_increment_pct: '',
  });
  const [saving, setSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    if (!cfg) {
      const dates = inferSubscriptionDates(term.installments);
      setSetupForm(f => ({
        ...f,
        start_date: dates.start_date || f.start_date,
        end_date: dates.end_date || f.end_date,
      }));
    }
  }, [term.term_id, term.installments, cfg]);

  const handleSaveConfig = async () => {
    if (!setupForm.start_date) return toast.error('Start date is required');
    setSaving(true);
    try {
      await upsertSubscriptionConfig(term.term_id, {
        ...setupForm,
        yoy_increment_pct: setupForm.yoy_increment_pct ? parseFloat(setupForm.yoy_increment_pct) : null,
        renewal_notice_days: parseInt(setupForm.renewal_notice_days, 10) || 30,
        cancellation_notice_days: parseInt(setupForm.cancellation_notice_days, 10) || 30,
      });
      toast.success('Renewal settings saved');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save renewal settings');
    } finally {
      setSaving(false);
    }
  };

  const runRenewalAction = async (action, reason) => {
    if (!cfg?.config_id) return;
    setActionLoading(action);
    try {
      if (action === 'renew') {
        const res = await renewSubscription(cfg.config_id);
        toast.success(res.data.message || 'Subscription renewed');
      } else if (action === 'approve') {
        await approveRenewal(cfg.config_id);
        toast.success('Renewal approved');
      } else if (action === 'reject') {
        await rejectRenewal(cfg.config_id, { reason: reason || 'Rejected by user' });
        toast.success('Renewal rejected — subscription closed');
      } else if (action === 'cancel') {
        await cancelSubscription(cfg.config_id, { reason: reason || 'Cancelled by user' });
        toast.success('Subscription cancelled');
      }
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Action failed');
    } finally {
      setActionLoading(null);
    }
  };

  const isCancelled = !!cfg?.cancelled_at;
  const configId = cfg?.config_id;

  return (
    <div className="card p-5">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="font-semibold">Subscription &amp; Renewal</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            Configure renewal policy and trigger renew, approve, or cancel actions.
          </p>
        </div>
        {cfg?.renewal_mode && (
          <span className="badge bg-blue-100 text-blue-800 whitespace-nowrap">
            {renewalModeLabel(cfg.renewal_mode)}
          </span>
        )}
      </div>

      {!cfg ? (
        <div className="p-4 bg-blue-50 border border-blue-100 rounded-xl space-y-4">
          <p className="text-sm text-blue-900">
            This subscription has no renewal policy yet. Set dates and renewal mode below
            (dates are pre-filled from installment schedule when available).
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <label className="label text-xs">Start Date</label>
              <input type="date" className="input"
                value={setupForm.start_date}
                onChange={e => setSetupForm(f => ({ ...f, start_date: e.target.value }))}/>
            </div>
            <div>
              <label className="label text-xs">End Date</label>
              <input type="date" className="input"
                value={setupForm.end_date}
                min={setupForm.start_date}
                onChange={e => setSetupForm(f => ({ ...f, end_date: e.target.value }))}/>
            </div>
            <div>
              <label className="label text-xs">Billing Cycle</label>
              <select className="select" value={setupForm.billing_cycle}
                onChange={e => setSetupForm(f => ({ ...f, billing_cycle: e.target.value }))}>
                <option value="MONTHLY">Monthly</option>
                <option value="QUARTERLY">Quarterly</option>
                <option value="HALF_YEARLY">Half Yearly</option>
                <option value="ANNUAL">Annual</option>
              </select>
            </div>
            <div>
              <label className="label text-xs">Renewal Mode</label>
              <select className="select" value={setupForm.renewal_mode}
                onChange={e => setSetupForm(f => ({ ...f, renewal_mode: e.target.value }))}>
                {RENEWAL_MODES.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label text-xs">Renewal Notice (days)</label>
              <input type="number" min={1} className="input"
                value={setupForm.renewal_notice_days}
                onChange={e => setSetupForm(f => ({ ...f, renewal_notice_days: e.target.value }))}/>
            </div>
            <div>
              <label className="label text-xs">YoY Increment (%)</label>
              <input type="number" step="0.01" min={0} className="input"
                placeholder="Optional"
                value={setupForm.yoy_increment_pct}
                onChange={e => setSetupForm(f => ({ ...f, yoy_increment_pct: e.target.value }))}/>
            </div>
          </div>
          <button onClick={handleSaveConfig} disabled={saving} className="btn-primary text-sm">
            {saving ? 'Saving…' : 'Save Renewal Settings'}
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
            {[
              { label: 'Billing Cycle', value: cfg.billing_cycle },
              { label: 'Billing in Advance', value: cfg.billing_in_advance ? 'Yes' : 'No (arrears)' },
              { label: 'Renewal Mode', value: renewalModeLabel(cfg.renewal_mode) },
              { label: 'Renewals Count', value: cfg.auto_renewal_count ?? 0 },
              { label: 'YoY Increment', value: cfg.yoy_increment_pct ? `${cfg.yoy_increment_pct}%` : 'None' },
              { label: 'Start Date', value: formatDate(cfg.start_date) },
              { label: 'End Date', value: formatDate(cfg.end_date) },
              { label: 'Renewal Notice', value: `${cfg.renewal_notice_days} days` },
              { label: 'Cancellation Notice', value: `${cfg.cancellation_notice_days} days` },
            ].map(f => (
              <div key={f.label}>
                <div className="text-xs text-gray-400">{f.label}</div>
                <div className="font-medium mt-0.5">{f.value ?? '—'}</div>
              </div>
            ))}
          </div>

          {isCancelled && (
            <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-lg text-sm text-red-800">
              Subscription cancelled on {formatDate(cfg.cancelled_at)}
            </div>
          )}

          {cfg.year_pricing?.length > 0 && (
            <div className="mb-4 pt-4 border-t">
              <h4 className="text-sm font-semibold mb-2">Year-by-Year Pricing</h4>
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    {['Year', 'Annual Fee', 'Effective From', 'Effective To'].map(h => (
                      <th key={h} className="table-th">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cfg.year_pricing.map(yp => (
                    <tr key={yp.pricing_id}>
                      <td className="table-td">Year {yp.year_no}</td>
                      <td className="table-td font-semibold text-green-700">{formatCurrency(yp.annual_amount, term.currency)}</td>
                      <td className="table-td">{formatDate(yp.effective_from)}</td>
                      <td className="table-td">{formatDate(yp.effective_to)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!isCancelled && configId && (
            <div className="flex flex-wrap gap-2 pt-4 border-t">
              {cfg.renewal_mode === 'AUTO' && (
                <button type="button" className="btn-primary text-sm"
                  disabled={!!actionLoading}
                  onClick={() => runRenewalAction('renew')}>
                  <RefreshCw size={14} className={actionLoading === 'renew' ? 'animate-spin' : ''}/>
                  {actionLoading === 'renew' ? 'Renewing…' : 'Renew Now'}
                </button>
              )}
              {cfg.renewal_mode === 'BUYER_APPROVAL_REQUIRED' && (
                <>
                  <button type="button" className="btn-primary text-sm"
                    disabled={!!actionLoading}
                    onClick={() => runRenewalAction('approve')}>
                    <CheckCircle size={14}/>
                    {actionLoading === 'approve' ? 'Approving…' : 'Approve Renewal'}
                  </button>
                  <button type="button" className="btn-secondary text-sm text-red-600 border-red-200"
                    disabled={!!actionLoading}
                    onClick={() => {
                      const reason = window.prompt('Reason for rejecting renewal (optional):');
                      if (reason !== null) runRenewalAction('reject', reason);
                    }}>
                    <XCircle size={14}/>
                    {actionLoading === 'reject' ? 'Rejecting…' : 'Reject Renewal'}
                  </button>
                </>
              )}
              <button type="button" className="btn-secondary text-sm text-red-600 border-red-200"
                disabled={!!actionLoading}
                onClick={() => {
                  const reason = window.prompt('Reason for cancellation (optional):');
                  if (reason !== null) runRenewalAction('cancel', reason);
                }}>
                Cancel Subscription
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function TermEditModal({ term, onClose, onSaved }) {
  const [name, setName] = useState(term.name || '');
  const [installments, setInstallments] = useState(
    (term.installments || []).map(i => ({
      sequence_no: i.sequence_no,
      label: i.label,
      amount_type: i.amount_type,
      amount_value: i.amount_value,
      trigger_type: i.trigger_type,
      anchor_date_type: i.anchor_date_type,
      due_offset_days: i.due_offset_days || 0,
      scheduled_invoice_date: i.scheduled_invoice_date ? String(i.scheduled_invoice_date).split('T')[0] : '',
      grace_period_days: i.grace_period_days || 0,
      requires_buyer_approval: i.requires_buyer_approval || false,
    }))
  );
  const [saving, setSaving] = useState(false);
  const totalPct = installments.filter(i => i.amount_type === 'PERCENTAGE').reduce((s, i) => s + parseFloat(i.amount_value || 0), 0);
  const pctValid = installments.every(i => i.amount_type === 'FIXED_AMOUNT') || Math.abs(totalPct - 100) <= 0.01;

  const handleSave = async (e) => {
    e.preventDefault();
    if (!pctValid) return toast.error(`Installments must sum to 100% (current: ${totalPct.toFixed(2)}%)`);
    setSaving(true);
    try {
      await updatePaymentTerm(term.term_id, { name, term_type: term.term_type, installments });
      toast.success('Payment term updated and synced to O2C');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppModal title={`Edit ${term.name}`} onClose={onClose} maxWidth={640}>
      <form onSubmit={handleSave} className="space-y-4">
        <div>
          <label className="label">Name</label>
          <input className="input" value={name} onChange={e => setName(e.target.value)} required />
        </div>
        <div className="text-xs text-gray-500">
          Installments: {totalPct.toFixed(2)}% {!pctValid && <span className="text-red-500">(must be 100%)</span>}
        </div>
        <div className="max-h-56 overflow-y-auto space-y-2">
          {installments.map((inst, idx) => (
            <div key={idx} className="grid grid-cols-3 gap-2 text-sm">
              <input className="input" value={inst.label}
                onChange={e => setInstallments(prev => prev.map((x, i) => i === idx ? { ...x, label: e.target.value } : x))} />
              <select className="select" value={inst.amount_type}
                onChange={e => setInstallments(prev => prev.map((x, i) => i === idx ? { ...x, amount_type: e.target.value } : x))}>
                <option value="PERCENTAGE">%</option>
                <option value="FIXED_AMOUNT">Fixed</option>
              </select>
              <input type="number" className="input" value={inst.amount_value}
                onChange={e => setInstallments(prev => prev.map((x, i) => i === idx ? { ...x, amount_value: e.target.value } : x))} />
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button type="submit" disabled={saving} className="btn-primary">{saving ? 'Saving…' : 'Save & Sync'}</button>
        </div>
      </form>
    </AppModal>
  );
}

// ═══════════════════════════════════════════════════════════════
// TERM FORM MODAL  (create / use from SO)
// ═══════════════════════════════════════════════════════════════
export function TermFormModal({ onClose, onCreated, preselectedPOId = null }) {
  const [step, setStep]     = useState(1); // 1=details, 2=installments
  const [form, setForm]     = useState({
    term_type: 'DELIVERY_BASED',
    name: '',
    description: '',
    is_template: false,
    currency: 'INR',
    invoice_approval_mode: 'DRAFT_FOR_REVIEW',
    po_id: preselectedPOId || '',
  });

  // ── Period Tranche wizard state ──────────────────────────────
  const [trancheCount, setTrancheCount]     = useState(null); // null = not yet asked
  const [trancheCountInput, setTrancheCountInput] = useState(3);

  // ── Subscription wizard state ────────────────────────────────
  const [subConfig, setSubConfig] = useState({
    period_from: '',
    period_to: '',
    periodicity: 'MONTHLY',
    amount_type: 'FIXED',
    first_amount: '',
    increment: '',
    renewal_mode: 'AUTO',
    renewal_notice_days: 30,
    cancellation_notice_days: 30,
    yoy_increment_pct: '',
    billing_in_advance: true,
  });
  const [subReady, setSubReady] = useState(false); // subscription wizard completed

  const [installments, setInstallments] = useState([{
    sequence_no: 1, label: 'Full Payment', amount_type: 'PERCENTAGE',
    amount_value: 100, trigger_type: 'DELIVERY_ACCEPTANCE',
    anchor_date_type: 'DELIVERY_DATE', due_offset_days: 0,
    scheduled_invoice_date: '',
    grace_period_days: 0, requires_buyer_approval: false,
  }]);

  const [pos, setPOs]       = useState([]);
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    Promise.all([getPOs(), getTemplates()]).then(([p, t]) => {
      setPOs(p.data.purchase_orders || []);
      setTemplates(t.data.templates || []);
    }).catch(() => {});
  }, []);

  const totalPct  = installments.filter(i => i.amount_type === 'PERCENTAGE').reduce((s, i) => s + parseFloat(i.amount_value || 0), 0);
  const allFixed  = installments.every(i => i.amount_type === 'FIXED_AMOUNT');
  const pctValid  = allFixed || Math.abs(totalPct - 100) <= 0.01;

  // ── Installment helpers ──────────────────────────────────────
  const addInstallment = (afterIdx = null) => {
    const newInst = {
      sequence_no: 0, label: 'New Installment', amount_type: 'PERCENTAGE',
      amount_value: 0, trigger_type: 'DELIVERY_ACCEPTANCE',
      anchor_date_type: 'DELIVERY_DATE', due_offset_days: 0,
      scheduled_invoice_date: '', grace_period_days: 0, requires_buyer_approval: false,
    };
    setInstallments(prev => {
      let next;
      if (afterIdx === null) {
        next = [...prev, newInst];
      } else {
        next = [...prev.slice(0, afterIdx + 1), newInst, ...prev.slice(afterIdx + 1)];
      }
      return next.map((inst, i) => ({ ...inst, sequence_no: i + 1 }));
    });
  };

  const removeInstallment = (idx) =>
    setInstallments(prev =>
      prev.filter((_, i) => i !== idx).map((inst, i) => ({ ...inst, sequence_no: i + 1 }))
    );

  const updateInstallment = (idx, key, val) =>
    setInstallments(prev =>
      prev.map((inst, i) => i === idx ? { ...inst, [key]: val } : inst)
    );

  // ── Period Tranche auto-generate ─────────────────────────────
  const applyPeriodTranche = (count) => {
    const pct = parseFloat((100 / count).toFixed(2));
    const generated = Array.from({ length: count }, (_, i) => ({
      sequence_no: i + 1,
      label: `Tranche ${i + 1}`,
      amount_type: 'PERCENTAGE',
      amount_value: i === count - 1 ? (100 - pct * (count - 1)) : pct, // last gets remainder
      trigger_type: 'TIME_PERIOD',
      anchor_date_type: 'PO_CONFIRM_DATE',
      due_offset_days: (i + 1) * 30,
      scheduled_invoice_date: '',
      grace_period_days: 0,
      requires_buyer_approval: false,
    }));
    setInstallments(generated);
    setTrancheCount(count);
  };

  // ── Subscription auto-generate ───────────────────────────────
  const generateSubscriptionInstallments = () => {
    if (!subConfig.period_from || !subConfig.period_to || !subConfig.first_amount) {
      toast.error('Please fill all subscription fields');
      return;
    }
    const from  = new Date(subConfig.period_from);
    const to    = new Date(subConfig.period_to);
    const dates = [];

    const addPeriods = (d) => {
      const next = new Date(d);
      if (subConfig.periodicity === 'DAILY')     next.setDate(next.getDate() + 1);
      else if (subConfig.periodicity === 'WEEKLY') next.setDate(next.getDate() + 7);
      else if (subConfig.periodicity === 'MONTHLY') next.setMonth(next.getMonth() + 1);
      else if (subConfig.periodicity === 'QUARTERLY') next.setMonth(next.getMonth() + 3);
      else if (subConfig.periodicity === 'YEARLY') next.setFullYear(next.getFullYear() + 1);
      return next;
    };

    let cur = new Date(from);
    while (cur <= to) {
      dates.push(new Date(cur));
      cur = addPeriods(cur);
    }

    if (dates.length === 0) { toast.error('No periods generated — check dates'); return; }
    if (dates.length > 60)  { toast.error('Too many periods (max 60)'); return; }

    const base = parseFloat(subConfig.first_amount);
    const incr = parseFloat(subConfig.increment || 0);
    let amount = base;

    let generated = dates.map((d, i) => {
      if (i > 0 && subConfig.amount_type === 'FIXED_INCREMENT') amount += incr;
      const dateStr = d.toISOString().split('T')[0];
      const label = `${subConfig.periodicity.charAt(0) + subConfig.periodicity.slice(1).toLowerCase()} ${i + 1} — ${dateStr}`;
      return {
        sequence_no: i + 1,
        label,
        amount_type: 'FIXED_AMOUNT',
        amount_value: parseFloat(amount.toFixed(2)),
        trigger_type: 'TIME_PERIOD',
        anchor_date_type: 'PO_CONFIRM_DATE',
        due_offset_days: 0,
        scheduled_invoice_date: dateStr,
        subscription_period_label: `${subConfig.periodicity} ${i + 1} — ${dateStr}`,
        grace_period_days: 0,
        requires_buyer_approval: false,
      };
    });

    const linkedPo = pos.find(p => p.po_id === form.po_id);
    const poValue = linkedPo ? parseFloat(linkedPo.po_value) : null;
    if (poValue && generated.length > 0) {
      const sum = generated.reduce((s, g) => s + g.amount_value, 0);
      if (Math.abs(sum - poValue) > 0.01) {
        const scale = poValue / sum;
        let running = 0;
        generated = generated.map((g, i) => {
          const isLast = i === generated.length - 1;
          const amt = isLast
            ? parseFloat((poValue - running).toFixed(2))
            : parseFloat((g.amount_value * scale).toFixed(2));
          running += amt;
          return { ...g, amount_value: amt };
        });
        toast(`Installments scaled to SO value ${poValue.toLocaleString()}`, { icon: 'ℹ️' });
      }
    }

    setInstallments(generated);
    setSubReady(true);
    toast.success(`${generated.length} subscription installments generated`);
  };

  // ── Quick presets ────────────────────────────────────────────
  const applyPreset = (type) => {
    const presets = {
      'full_delivery':  [{ seq:1, label:'Full Payment on Delivery', pct:100, trigger:'DELIVERY_ACCEPTANCE', anchor:'DELIVERY_DATE', offset:0 }],
      'advance_50_50':  [{ seq:1, label:'Advance (50%)', pct:50, trigger:'PO_CONFIRMATION', anchor:'PO_CONFIRM_DATE', offset:0 }, { seq:2, label:'Balance on Delivery (50%)', pct:50, trigger:'DELIVERY_ACCEPTANCE', anchor:'DELIVERY_DATE', offset:0 }],
      'advance_30_70':  [{ seq:1, label:'Advance (30%)', pct:30, trigger:'PO_CONFIRMATION', anchor:'PO_CONFIRM_DATE', offset:0 }, { seq:2, label:'Balance on Delivery (70%)', pct:70, trigger:'DELIVERY_ACCEPTANCE', anchor:'DELIVERY_DATE', offset:0 }],
      'three_tranche':  [{ seq:1, label:'Advance (20%)', pct:20, trigger:'PO_CONFIRMATION', anchor:'PO_CONFIRM_DATE', offset:0 }, { seq:2, label:'On Delivery (75%)', pct:75, trigger:'DELIVERY_ACCEPTANCE', anchor:'DELIVERY_DATE', offset:0 }, { seq:3, label:'Retention (5%)', pct:5, trigger:'TIME_PERIOD', anchor:'DELIVERY_DATE', offset:90 }],
      'net30':          [{ seq:1, label:'Full Payment Net 30', pct:100, trigger:'DELIVERY_ACCEPTANCE', anchor:'DELIVERY_DATE', offset:30 }],
      'net60':          [{ seq:1, label:'Full Payment Net 60', pct:100, trigger:'DELIVERY_ACCEPTANCE', anchor:'DELIVERY_DATE', offset:60 }],
    };
    const p = presets[type];
    if (p) setInstallments(p.map(x => ({ sequence_no: x.seq, label: x.label, amount_type: 'PERCENTAGE', amount_value: x.pct, trigger_type: x.trigger, anchor_date_type: x.anchor, due_offset_days: x.offset, scheduled_invoice_date: '', grace_period_days: 0, requires_buyer_approval: false })));
  };

  const handleSave = async () => {
    if (!form.name) return toast.error('Payment term name is required');
    if (!pctValid)  return toast.error(`Installments must sum to 100% (current: ${totalPct.toFixed(2)}%)`);
    setSaving(true);
    try {
      const payload = { ...form, installments };
      if (form.term_type === 'SUBSCRIPTION' && subConfig.period_from) {
        payload.subscription_config = buildSubscriptionConfigPayload(subConfig);
      }
      const result = await createPaymentTerm(payload);
      const termId = result.data.term_id;
      if (form.po_id) {
        await bindPaymentTerm(termId, form.po_id);
        toast.success('Payment term created and linked to Sales Order!');
      } else {
        toast.success('Payment term created');
      }
      onCreated(termId);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create payment term');
    } finally { setSaving(false); }
  };

  // Decide what step 2 looks like based on term_type
  const isPeriodTranche  = form.term_type === 'PERIOD_TRANCHE';
  const isSubscription   = form.term_type === 'SUBSCRIPTION';

  return (
    <AppModal
      title={step === 1 ? 'New Payment Term — Details' : 'New Payment Term — Installments'}
      subtitle={`Step ${step} of 2`}
      onClose={onClose}
      maxWidth={768}
    >

        {/* ── STEP 1: Details ──────────────────────────────── */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="p-4 bg-primary-50 border border-primary-100 rounded-xl">
              <label className="label text-primary-700">
                Link to Sales Order <span className="font-normal text-gray-500">(recommended)</span>
              </label>
              <select className="select" value={form.po_id} onChange={e => {
                setF('po_id', e.target.value);
                const po = pos.find(p => p.po_id === e.target.value);
                if (po?.currency) setF('currency', po.currency);
              }}>
                <option value="">— Select Sales Order (or leave blank to create unlinked term) —</option>
                {pos.filter(p => ['DRAFT','PENDING','APPROVED','CONFIRMED','ACTIVE'].includes(p.status)).map(p => (
                  <option key={p.po_id} value={p.po_id}>
                    {p.po_number} — {p.title} — {p.buyer_name || '?'} — {formatCurrency(p.po_value, p.currency)}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Payment Term Name <span className="text-red-500">*</span></label>
                <input className="input" value={form.name} onChange={e => setF('name', e.target.value)}
                  placeholder="e.g. Quarterly Subscription 2026" autoFocus/>
              </div>
              <div>
                <label className="label">Term Type</label>
                <select className="select" value={form.term_type} onChange={e => {
                  setF('term_type', e.target.value);
                  setTrancheCount(null);
                  setSubReady(false);
                }}>
                  {TERM_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Currency</label>
                <select className="select" value={form.currency} onChange={e => setF('currency', e.target.value)}>
                  {['INR','USD','EUR','GBP','AED','SGD'].map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Invoice Approval Mode</label>
                <select className="select" value={form.invoice_approval_mode} onChange={e => setF('invoice_approval_mode', e.target.value)}>
                  <option value="AUTO_APPROVE">Auto Approve</option>
                  <option value="DRAFT_FOR_REVIEW">Draft for Review</option>
                  <option value="DRAFT_WITH_SLA">Draft with SLA</option>
                </select>
              </div>
            </div>
            <div>
              <label className="label">Description</label>
              <textarea className="input" rows={2} value={form.description}
                onChange={e => setF('description', e.target.value)}
                placeholder="Optional notes about this payment arrangement"/>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="is_tpl" checked={form.is_template}
                onChange={e => setF('is_template', e.target.checked)} className="rounded"/>
              <label htmlFor="is_tpl" className="text-sm text-gray-700">Save as reusable template</label>
            </div>

            {/* Term type hints */}
            {isPeriodTranche && (
              <div className="p-3 bg-amber-50 border border-amber-100 rounded-xl text-xs text-amber-800">
                <strong>Period Tranche:</strong> You'll specify the number of equal tranches on the next step.
                Each tranche gets a Time Period trigger with a scheduled invoice date — on that date a draft invoice is raised automatically.
              </div>
            )}
            {isSubscription && (
              <div className="p-3 bg-blue-50 border border-blue-100 rounded-xl text-xs text-blue-800">
                <strong>Subscription:</strong> You'll configure the subscription period, periodicity, and amount on the next step.
                Each period gets a scheduled trigger date — draft invoices are raised automatically.
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button onClick={onClose} className="btn-secondary flex-1 justify-center">Cancel</button>
              <button onClick={() => setStep(2)} disabled={!form.name} className="btn-primary flex-1 justify-center">
                Next: Define Installments →
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 2: Installments ──────────────────────────── */}
        {step === 2 && (
          <div className="space-y-4">

            {/* ── PERIOD TRANCHE WIZARD ── */}
            {isPeriodTranche && trancheCount === null && (
              <div className="p-5 bg-amber-50 border border-amber-200 rounded-xl">
                <h4 className="font-semibold text-amber-900 mb-1">How many tranches?</h4>
                <p className="text-xs text-amber-700 mb-3">
                  The system will create equal installments, each with a Time Period trigger and a scheduled invoice date field.
                  You can adjust individual amounts and dates after generation.
                </p>
                <div className="flex items-center gap-3">
                  <input type="number" min={2} max={24} className="input w-28"
                    value={trancheCountInput}
                    onChange={e => setTrancheCountInput(Math.max(2, Math.min(24, parseInt(e.target.value) || 2)))}/>
                  <button onClick={() => applyPeriodTranche(trancheCountInput)}
                    className="btn-primary">
                    Generate {trancheCountInput} Tranches →
                  </button>
                </div>
              </div>
            )}

            {/* ── SUBSCRIPTION WIZARD ── */}
            {isSubscription && !subReady && (
              <div className="p-5 bg-blue-50 border border-blue-200 rounded-xl space-y-3">
                <h4 className="font-semibold text-blue-900">Subscription Configuration</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label text-xs">Subscription Start Date <span className="text-red-500">*</span></label>
                    <input type="date" className="input"
                      value={subConfig.period_from}
                      onChange={e => setSubConfig(s => ({ ...s, period_from: e.target.value }))}/>
                  </div>
                  <div>
                    <label className="label text-xs">Subscription End Date <span className="text-red-500">*</span></label>
                    <input type="date" className="input"
                      value={subConfig.period_to}
                      min={subConfig.period_from}
                      onChange={e => setSubConfig(s => ({ ...s, period_to: e.target.value }))}/>
                  </div>
                  <div>
                    <label className="label text-xs">Periodicity <span className="text-red-500">*</span></label>
                    <select className="select" value={subConfig.periodicity}
                      onChange={e => setSubConfig(s => ({ ...s, periodicity: e.target.value }))}>
                      <option value="DAILY">Daily</option>
                      <option value="WEEKLY">Weekly</option>
                      <option value="MONTHLY">Monthly</option>
                      <option value="QUARTERLY">Quarterly</option>
                      <option value="YEARLY">Yearly</option>
                    </select>
                  </div>
                  <div>
                    <label className="label text-xs">Amount Type</label>
                    <select className="select" value={subConfig.amount_type}
                      onChange={e => setSubConfig(s => ({ ...s, amount_type: e.target.value }))}>
                      <option value="FIXED">Fixed Amount (same every period)</option>
                      <option value="FIXED_INCREMENT">Fixed + Period-on-Period Increment</option>
                    </select>
                  </div>
                  <div>
                    <label className="label text-xs">
                      {subConfig.amount_type === 'FIXED_INCREMENT' ? 'First Period Amount' : 'Amount per Period'} ({form.currency}) <span className="text-red-500">*</span>
                    </label>
                    <input type="number" step="0.01" className="input"
                      value={subConfig.first_amount}
                      onChange={e => setSubConfig(s => ({ ...s, first_amount: e.target.value }))}
                      placeholder="e.g. 10000"/>
                  </div>
                  {subConfig.amount_type === 'FIXED_INCREMENT' && (
                    <div>
                      <label className="label text-xs">Increment per Period ({form.currency})</label>
                      <input type="number" step="0.01" className="input"
                        value={subConfig.increment}
                        onChange={e => setSubConfig(s => ({ ...s, increment: e.target.value }))}
                        placeholder="e.g. 500"/>
                    </div>
                  )}
                </div>

                <div className="pt-3 border-t border-blue-200">
                  <h5 className="text-sm font-semibold text-blue-900 mb-2">Renewal Policy</h5>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="label text-xs">Renewal Mode</label>
                      <select className="select" value={subConfig.renewal_mode}
                        onChange={e => setSubConfig(s => ({ ...s, renewal_mode: e.target.value }))}>
                        {RENEWAL_MODES.map(m => (
                          <option key={m.value} value={m.value}>{m.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="label text-xs">Renewal Notice (days)</label>
                      <input type="number" min={1} className="input"
                        value={subConfig.renewal_notice_days}
                        onChange={e => setSubConfig(s => ({ ...s, renewal_notice_days: e.target.value }))}/>
                    </div>
                    {subConfig.renewal_mode === 'AUTO' && (
                      <div>
                        <label className="label text-xs">YoY Increment (%)</label>
                        <input type="number" step="0.01" min={0} className="input"
                          placeholder="Optional"
                          value={subConfig.yoy_increment_pct}
                          onChange={e => setSubConfig(s => ({ ...s, yoy_increment_pct: e.target.value }))}/>
                      </div>
                    )}
                    <div className="flex items-end">
                      <label className="flex items-center gap-2 text-sm text-gray-700 pb-2">
                        <input type="checkbox" className="rounded"
                          checked={subConfig.billing_in_advance}
                          onChange={e => setSubConfig(s => ({ ...s, billing_in_advance: e.target.checked }))}/>
                        Bill in advance
                      </label>
                    </div>
                  </div>
                </div>

                <button onClick={generateSubscriptionInstallments} className="btn-primary">
                  Generate Subscription Installments →
                </button>
              </div>
            )}

            {/* Show installments if wizard done or non-wizard type */}
            {(!isPeriodTranche || trancheCount !== null) && (!isSubscription || subReady) && (
              <>
                {/* Quick presets — only for non-wizard types */}
                {!isPeriodTranche && !isSubscription && (
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Quick Presets</p>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { key: 'full_delivery', label: '100% on Delivery' },
                        { key: 'advance_50_50', label: '50/50 Advance+Delivery' },
                        { key: 'advance_30_70', label: '30/70 Advance+Delivery' },
                        { key: 'three_tranche', label: '20/75/5 with Retention' },
                        { key: 'net30', label: 'Net 30' },
                        { key: 'net60', label: 'Net 60' },
                      ].map(p => (
                        <button key={p.key} onClick={() => applyPreset(p.key)}
                          className="px-3 py-1.5 bg-gray-100 hover:bg-primary-100 hover:text-primary-700 text-gray-600 text-xs rounded-lg transition-colors">
                          {p.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Percentage total bar */}
                {!allFixed && (
                  <div className={`px-4 py-2.5 rounded-lg text-sm font-medium ${pctValid ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-700 border border-red-100'}`}>
                    Total: {totalPct.toFixed(2)}% {pctValid ? '✅ Sums to 100%' : '⚠️ Must sum to 100%'}
                  </div>
                )}

                {/* Installment rows — scrollable when many */}
                <div className="space-y-3 overflow-y-auto" style={{ maxHeight: '50vh', paddingRight: 4 }}>
                {installments.map((inst, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-xl p-4 space-y-3 bg-gray-50/50">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm text-gray-700">
                        Installment {inst.sequence_no}
                        {inst.trigger_type === 'TIME_PERIOD' && inst.scheduled_invoice_date && (
                          <span className="ml-2 text-xs font-normal text-amber-600">
                            📅 Auto-invoice on {inst.scheduled_invoice_date}
                          </span>
                        )}
                      </span>
                      <div className="flex items-center gap-2">
                        <button onClick={() => addInstallment(idx)}
                          title="Insert installment after this one"
                          className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-primary-100 text-gray-500 hover:text-primary-700">
                          + Insert after
                        </button>
                        {installments.length > 1 && (
                          <button onClick={() => removeInstallment(idx)} className="text-red-400 hover:text-red-600">
                            <Trash2 size={14}/>
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <div className="md:col-span-1">
                        <label className="label text-xs">Label</label>
                        <input className="input text-sm" value={inst.label}
                          onChange={e => updateInstallment(idx, 'label', e.target.value)}/>
                      </div>
                      <div>
                        <label className="label text-xs">Amount Type</label>
                        <select className="select text-sm" value={inst.amount_type}
                          onChange={e => updateInstallment(idx, 'amount_type', e.target.value)}>
                          <option value="PERCENTAGE">Percentage (%)</option>
                          <option value="FIXED_AMOUNT">Fixed Amount</option>
                        </select>
                      </div>
                      <div>
                        <label className="label text-xs">{inst.amount_type === 'PERCENTAGE' ? 'Percentage (%)' : `Amount (${form.currency})`}</label>
                        <input type="number" step="0.01" className="input text-sm" value={inst.amount_value}
                          onChange={e => updateInstallment(idx, 'amount_value', e.target.value)}/>
                      </div>
                      <div>
                        <label className="label text-xs">Trigger</label>
                        <select className="select text-sm" value={inst.trigger_type}
                          onChange={e => updateInstallment(idx, 'trigger_type', e.target.value)}>
                          {TRIGGER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      </div>
                      {/* Scheduled Invoice Date — only shown for TIME_PERIOD trigger */}
                      {inst.trigger_type === 'TIME_PERIOD' && (
                        <div>
                          <label className="label text-xs">
                            Scheduled Invoice Date <span className="text-amber-500">*</span>
                          </label>
                          <input type="date" className="input text-sm"
                            value={inst.scheduled_invoice_date}
                            onChange={e => updateInstallment(idx, 'scheduled_invoice_date', e.target.value)}/>
                          <p className="text-xs text-amber-600 mt-0.5">Draft invoice auto-raised on this date</p>
                        </div>
                      )}
                      {inst.trigger_type !== 'TIME_PERIOD' && (
                        <div>
                          <label className="label text-xs">Anchor Date</label>
                          <select className="select text-sm" value={inst.anchor_date_type}
                            onChange={e => updateInstallment(idx, 'anchor_date_type', e.target.value)}>
                            {ANCHOR_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                          </select>
                        </div>
                      )}
                      {inst.trigger_type !== 'TIME_PERIOD' && (
                        <div>
                          <label className="label text-xs">Due Offset (days)</label>
                          <input type="number" min="0" className="input text-sm" value={inst.due_offset_days}
                            onChange={e => updateInstallment(idx, 'due_offset_days', parseInt(e.target.value) || 0)}/>
                        </div>
                      )}
                      <div>
                        <label className="label text-xs">Grace Period (days)</label>
                        <input type="number" min="0" className="input text-sm" value={inst.grace_period_days}
                          onChange={e => updateInstallment(idx, 'grace_period_days', parseInt(e.target.value) || 0)}/>
                      </div>
                    </div>
                  </div>
                ))}
                </div>{/* end scrollable installment list */}

                <button onClick={() => addInstallment()} className="btn-secondary w-full justify-center text-sm">
                  <PlusCircle size={15}/> Add Installment at End
                </button>

                <div className="flex gap-3 pt-2">
                  <button onClick={() => { setStep(1); setTrancheCount(null); setSubReady(false); }}
                    className="btn-secondary flex-1 justify-center">← Back</button>
                  <button onClick={handleSave} disabled={saving || !pctValid} className="btn-primary flex-1 justify-center">
                    {saving ? 'Saving…' : form.po_id ? '✅ Create & Link to Sales Order' : 'Create Payment Term'}
                  </button>
                </div>
              </>
            )}

            {/* Back button when wizard not yet completed */}
            {((isPeriodTranche && trancheCount === null) || (isSubscription && !subReady)) && (
              <button onClick={() => setStep(1)} className="btn-secondary text-sm">← Back to Details</button>
            )}
          </div>
        )}
    </AppModal>
  );
}

// Keep backward compat alias
function CreateTermModal(props) { return <TermFormModal {...props} />; }

